import { Suspense } from 'react';

import { initTagManager } from '@skyscanner-internal/gtm-client';
import { ROOT, SEARCH_WITH_AI, TRANSPORTS } from '@web-platform/shared-banana-constants/src/routes';
import { PAGE_GROUPS } from '@web-platform/shared-universal-constants/src/pageGroups';
import {
  BIFM_SUFFIX,
  CONFIG_SUFFIX,
  ITINERARY_SUFFIX,
  ITINERARY_PATH_SEGMENT,
  DAYVIEW,
} from '@web-platform/shared-universal-constants/src/routes';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { LoadingSpinner } from '@web-platform/shared-universal-features-components/src/LoadingSpinner/LoadingSpinner';
import { getLocation } from '@web-platform/shared-universal-redux/src/state';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import { useComponentWillMount } from '@web-platform/shared-universal-utils-frontend/src/helpers/hooks/useComponentWillMount';
import { isAcornRequest } from '@web-platform/shared-universal-utils-frontend/src/helpers/isAcornRequest';
import { lazyWithRetry } from '@web-platform/shared-universal-utils-frontend/src/helpers/lazyLoading';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';
import { useSelector } from 'react-redux';
import { Outlet, Route, Routes as RouterRoutes, useLocation } from 'react-router-dom';

import { PageAddons } from './PageAddons';
import { useInitialiseHistoryState } from './hooks/useInitialiseHistoryState';
import { useNewRelicCustomAttributes } from './hooks/useNewRelicCustomAttributes';
import { useSyncFareAttributeFiltersWithLocation } from './hooks/useSyncFareAttributeFiltersWithLocation';
import { useSyncReduxStateWithUrlLocation } from './hooks/useSyncReduxStateWithUrlLocation';
import { useUpdateVirtualReferrer } from './hooks/useUpdateVirtualReferrer';
import { RouterFallbackUI } from './utils/RouterFallbackUI';

import type { RouteType } from './types';

import STYLES from './Routes.module.scss';
import SPINNER_STYLES from '@web-platform/shared-universal-features-components/src/LoadingSpinner/LoadingSpinner.module.scss';

if (isAcornRequest()) {
  import('@web-platform/shared-acorn-utils-frontend/src/scss/global.module.scss');
}

const CombinedEntryPage = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "CombinedEntryPage" */
      './CombinedEntryPage'
    ),
);

const ExploreAI = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "ExploreAI" */
      '@web-platform/explore-ai'
    ),
);

const FeatureToggle = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "FeatureToggle" */
      '@web-platform/shared-universal-features-components/src/FeatureToggle'
    ),
);

const FlightsDayView = lazyWithRetry(
  () =>
    import(
      // webpackChunkName: "FlightsDayView"
      '@web-platform/flights-day-view'
    ),
);
const FlightsBookingPanelAcorn = lazyWithRetry(
  () =>
    import(
      // webpackChunkName: "FlightsBookingPanelAcorn"
      '@web-platform/flights-booking-panel-acorn'
    ),
);
const DetailsPanelEntry = lazyWithRetry(() =>
  import('@web-platform/flights-booking-panel').then(
    ({ DetailsPanelEntry: DetailsPanelEntryComponent }) => ({
      default: DetailsPanelEntryComponent,
    }),
  ),
);

const itineraryTokenPathRegex = new RegExp(`/${ITINERARY_PATH_SEGMENT}[^/]+/?$`);

const BananaDetailsPanelEntry = () => {
  const { pathname } = useLocation();
  const isTokenURL = itineraryTokenPathRegex.test(pathname);

  return <DetailsPanelEntry isTokenURL={isTokenURL} />;
};
const FlightsBifmPageAcorn = lazyWithRetry(
  () =>
    import(
      // webpackChunkName: "FlightsBifmPageAcorn"
      '@web-platform/flights-booking-panel-acorn/bifm'
    ),
);

const dayviewRoutes: RouteType[] = DAYVIEW.map((acornRoute) => ({
  path: `${acornRoute}/*`,
  name: 'Dayview',
  Component: FlightsDayView,
  hasScrollToTop: false,
  pageGroupName: PAGE_GROUPS.DAY_VIEW,
}));

const bookingPanelRoutes: RouteType[] = DAYVIEW.map((acornRoute) => ({
  path: `${acornRoute}${CONFIG_SUFFIX}`,
  name: 'booking-panel',
  Component: FlightsBookingPanelAcorn,
  hasScrollToTop: false,
  pageGroupName: PAGE_GROUPS.BOOKING_PANEL_ACORN,
}));

const bookingPanelItineraryRoutes: RouteType[] = DAYVIEW.map((acornRoute) => ({
  path: `${acornRoute}${ITINERARY_SUFFIX}`,
  name: 'booking-panel',
  Component: FlightsBookingPanelAcorn,
  hasScrollToTop: false,
  pageGroupName: PAGE_GROUPS.BOOKING_PANEL_ACORN,
}));

const bananaBookingPanelConfigRoutes: RouteType[] = DAYVIEW.map((dayviewRoute) => ({
  path: `${dayviewRoute}${CONFIG_SUFFIX}`,
  name: 'booking-panel',
  Component: BananaDetailsPanelEntry,
  hasScrollToTop: false,
  pageGroupName: PAGE_GROUPS.BOOKING_PANEL,
}));

const bananaBookingPanelItineraryRoutes: RouteType[] = DAYVIEW.map((dayviewRoute) => ({
  path: `${dayviewRoute}${ITINERARY_SUFFIX}`,
  name: 'booking-panel',
  Component: BananaDetailsPanelEntry,
  hasScrollToTop: false,
  pageGroupName: PAGE_GROUPS.BOOKING_PANEL,
}));

const bifmRoutes: RouteType[] = DAYVIEW.map((acornRoute) => ({
  path: `${acornRoute}${BIFM_SUFFIX}`,
  name: 'bifm-page',
  Component: FlightsBifmPageAcorn,
  hasScrollToTop: false,
  pageGroupName: PAGE_GROUPS.BOOKING_PANEL_ACORN,
}));

const acornRoutes = dayviewRoutes.concat(
  bookingPanelRoutes,
  bookingPanelItineraryRoutes,
  bifmRoutes,
);

const combinedEntryTransportRoutes: RouteType[] = TRANSPORTS.map((transportPath: string) => ({
  path: transportPath,
  name: 'CombinedEntryPage',
  Component: CombinedEntryPage,
  hasScrollToTop: true,
  pageGroupName: PAGE_GROUPS.COMBINED_ENTRY_PAGE,
}));

const combinedEntryRoutes = [...combinedEntryTransportRoutes];

const homePageRoutes: RouteType[] = [
  {
    path: ROOT,
    name: 'Home',
    // Fast Refresh/ Hot Module Reloading, for JS breaks when the Homepage is imported and assigned to a variable. Importing inline is equivalent, as the import is wrapped in React.lazy, and solves Webpack's confusion, allowing Fast Refresh to work.
    Component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "Homepage" */
          '@web-platform/homepage'
        ),
    ),
    hasScrollToTop: true,
    pageGroupName: PAGE_GROUPS.HOME,
  },
];

const exploreAIRoute = {
  path: '/explore-ai',
  name: 'ExploreAI',
  Component: ExploreAI,
  hasScrollToTop: true,
  pageGroupName: PAGE_GROUPS.EXPLORE_AI,
};

const searchWithAIRoutes: RouteType[] = SEARCH_WITH_AI.map((path: string) => ({
  path,
  name: 'SearchWithAI',
  Component: ExploreAI,
  hasScrollToTop: true,
  pageGroupName: PAGE_GROUPS.SEARCH_WITH_AI,
}));

// The CiP route is used to match the culture in path requests, so we can point them to the Home screen.
// UMS is validating the culture in path route server-side, and redirecting to Not Found when the culture is not valid.
// The CiP route should be the last route in the list, so it does not conflict with any other routes.
const cultureInPathRoute: RouteType = {
  path: '/:market/:locale/:currency',
  name: 'Home',
  Component: lazyWithRetry(
    () =>
      import(
        /* webpackChunkName: "Homepage" */
        '@web-platform/homepage'
      ),
  ),
  hasScrollToTop: true,
  pageGroupName: PAGE_GROUPS.HOME,
};

const LocationHandler = ({ routes }: { routes: RouteType[] }) => {
  useUpdateVirtualReferrer();
  useInitialiseHistoryState();
  useSyncReduxStateWithUrlLocation();
  useSyncFareAttributeFiltersWithLocation();
  useNewRelicCustomAttributes(routes);

  return null;
};

const MaybeFeatureToggle = () => {
  const showFeatureToggle = process.env.NODE_ENV === 'development';
  if (showFeatureToggle) {
    return (
      <Suspense fallback={null}>
        <FeatureToggle />
      </Suspense>
    );
  }
  return null;
};

const NotFoundComponent = () => {
  const { pathname } = useLocation();

  logWarn({
    component: 'Router',
    message: `Unhandled path: ${pathname}`,
  });

  return null;
};

export const Routes = () => {
  const bananaBookingPanelRoutes = getFeatureDecision(
    FEATURE_DECISION_KEYS.BOOKING_PANEL_WITHOUT_MODAL,
  )
    ? [...bananaBookingPanelConfigRoutes, ...bananaBookingPanelItineraryRoutes]
    : [];

  const routes: RouteType[] = homePageRoutes.concat(
    // The paths of Dayview and CR are conflict, so we need to wrap the condition
    isAcornRequest()
      ? acornRoutes
      : [
          ...combinedEntryRoutes,
          ...bananaBookingPanelRoutes,
          ...searchWithAIRoutes,
          cultureInPathRoute,
          exploreAIRoute,
        ],
  );

  const { lastNavWasSoft } = useSelector(getLocation);
  const location = useLocation();
  // Stuff we want to do when URL changes

  useComponentWillMount(() => {
    // Google Tag Manager requires consent data before events are sent. If setting consent is done later in the flow then we create a race condition between setting consent and sending events, where 'screen loaded' events may be sent _before_ GTM has set consent, defaulting to no consent, so be lost.
    // We init GTM here before any screens are mounted, so before any tracking events are sent.
    const enableGoogleTagManager =
      getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_GOOGLE_TAG_MANAGER) &&
      getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_BANANA_GTM_INTEGRATION);
    if (enableGoogleTagManager) {
      const { market } = getCulture();
      initTagManager(market);
    }
  });

  const routerFallbackUI = isAcornRequest() ? (
    <>
      <div className="react-render-acorn-gcs-header-skeleton" />
      <div className="react-render-acorn-gcs-search-skeleton" />
      <LoadingSpinner spinnerClassName={SPINNER_STYLES.spinnerContainer} />
    </>
  ) : (
    RouterFallbackUI({ lastNavWasSoft, spinnerContainerClassName: SPINNER_STYLES.spinnerContainer })
  );

  return (
    <>
      <Suspense fallback={routerFallbackUI}>
        <RouterRoutes location={location}>
          <Route
            element={
              <>
                <LocationHandler routes={routes} />
                <Outlet />
              </>
            }
          >
            {routes.map(({ Component, hasScrollToTop, path }) => (
              <Route
                path={path}
                key={path}
                element={
                  <div className={STYLES.container}>
                    <Component />
                    <PageAddons hasScrollToTop={hasScrollToTop} />
                  </div>
                }
              />
            ))}
            <Route path="*" element={<NotFoundComponent />} />
          </Route>
        </RouterRoutes>
      </Suspense>
      <MaybeFeatureToggle />
    </>
  );
};
